import { AbstractControl, ValidatorFn, UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import * as _ from 'lodash';

export function ownershipValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    let percentage = 0;
    const parties: any = control.value;

    parties.forEach((party: any) => {
      const partyOwnership: number = party.ownership;
      percentage = Math.round(+percentage + +partyOwnership);
    });

    if (percentage > 100) return { ownershipMax: true };
    if (percentage == 0) return { ownershipMin: true };

    return (percentage === 100) ? null : { 'ownership': true };
  };
}

export function notSameEmailValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const fa = control as UntypedFormArray;
    const emails = _.map(fa.controls, (ctrl: UntypedFormGroup) => ctrl.value);
    const groups = _.groupBy(emails, 'email');
    const duplicate = _.find(groups, g => g.length > 1);
    return duplicate ? { 'duplicateEmails': _.first(duplicate).email } : null;
  };
}

export function notSameMobileNumbersValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const fa = control as UntypedFormArray;
    const mobiles = _.map(fa.controls, (ctrl: UntypedFormGroup) => {
      return _.uniqBy(_.filter(ctrl.value.phones, p => p.phoneType === 'mobile' && p.phoneNumber), 'phoneNumber');
    });
    const mobileNumbers = _.flatten(mobiles);
    const groups = _.groupBy(mobileNumbers, 'phoneNumber');
    const duplicate = _.find(groups, g => g.length > 1);
    return duplicate ? { 'duplicateMobiles': _.first(duplicate).phoneNumber } : null;
  };
}

export interface SameValueExceptions {
  name: String;
  maxCount: Number;
}

export function noSameValues(controlName, exceptions: SameValueExceptions[] = []): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const fa = control as UntypedFormArray;
    let values = _.filter(_.map(fa.controls, (ctrl: UntypedFormGroup) => ctrl.value[controlName]), v => !!v);
    if (exceptions && exceptions.length > 0) {
      const exceptionValues = _.map(exceptions, 'name');
      const maxReached = _.some(exceptions, e => {
        return _.filter(values, v => v === e.name).length > e.maxCount;
      });
      if (maxReached) {
        return { 'noSameValues': true };
      }
      // remove the exception values from the current list
      values = _.filter(values, v => !exceptionValues.includes(v));
      // merge max unique values based on exception value
    }
    const uniqueValues = _.uniq(values);
    const result = uniqueValues.length !== values.length ? { 'noSameValues': true } : null;
    return result;
  };
}

export function partnershipValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const allEmployments = control.value;
    const allSelfEmployedPartnerships = _.chain(allEmployments)
      .map('currentEmployments')
      .flatten()
      .filter(['status', 'self-employed'])
      .filter(['basis', 'self-employed'])
      .filter(['businessType', 'partnership'])
      .map((e) => _.pick(e, ['link', 'percentage']))
      .groupBy(e => e.link)
      .value();

    const error = _.find(_.keys(allSelfEmployedPartnerships),
      link => {
        const partnershipSum = _.chain(allSelfEmployedPartnerships[link])
          .map(e => {
            return Number(e.percentage || 0);
          })
          .sum()
          .value();
        return partnershipSum > 100;
      });

    const result = error ? { 'invalidPartnership': error } : null;
    return result;
  };
}

export function notSameCompanyRelationShipValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const SEPERATOR = '||';
    const fa = control as UntypedFormArray;
    const companyRelationships = _(fa.controls).map((ctrl: UntypedFormGroup) => ctrl.value).groupBy((item) => `${item.type}${SEPERATOR}${item.to}`).value();
    const duplicate = _.find(companyRelationships, g => g.length > 1);
    return duplicate ? { 'duplicateRelationships': _.first(duplicate) } : null;
  };
}

export function borrowerGuarantorValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const fa = control as UntypedFormArray;

    const isValidBorrowerGuarantor = !_.isEmpty(
      _.filter(
        fa.value,
        ({ borrowerGuarantor }) => borrowerGuarantor === 'borrower'
      )
    );
    return !isValidBorrowerGuarantor
      ? { invalidBorrowerGuarantorTypes: _.head(fa.controls) }
      : null;
  };
}
